import { useEffect, useRef } from 'react';
import { clamp } from '../utils/clamp';
export function useValue({ offset, minScaleOffset, ratio, min, max, value, movingLasts, isAnimatingExternally, onChange, setOffset, animateTo, calcOffsetWithRatio, }) {
    const prevValueRef = useRef(-1);
    const prevOffsetRef = useRef(offset);
    const hasInitializedRef = useRef(false);
    // Offset that an external (value-driven) positioning asked React to commit via
    // `setOffset`/`animateTo`. Until the `offset` state actually reflects it, an extra
    // effect run (e.g. React StrictMode's double-invoke, where refs persist but state
    // is still stale) would see the old `offset`, mistake it for a drag, and emit a
    // spurious min/max value. We hold here and skip until the commit lands.
    const pendingExternalOffsetRef = useRef(null);
    // Detect internal or external changes.
    useEffect(() => {
        // A pending external positioning hasn't committed yet: ignore the stale `offset`
        // until it does, unless a new external `value` arrived (which we must still apply).
        if (pendingExternalOffsetRef.current !== null) {
            if (offset === pendingExternalOffsetRef.current) {
                pendingExternalOffsetRef.current = null;
            }
            else if (value === prevValueRef.current) {
                return;
            }
        }
        // Helper for updating the value.
        const updateValue = (next, forced = false) => {
            next = clamp(next, min, max);
            if (forced || next !== prevValueRef.current) {
                prevValueRef.current = next;
                onChange(next);
            }
        };
        const handleExternalChange = () => {
            const nextOffset = calcOffsetWithRatio(value);
            if (nextOffset !== prevOffsetRef.current) {
                // There is a new offset - but still the change in value might be smaller then user clicked (due to clamp).
                prevOffsetRef.current = nextOffset;
                pendingExternalOffsetRef.current = nextOffset;
                updateValue(value);
                // First external change positions the slider at mount — must be instant,
                // not an animation from offset=0 to the initial value.
                if (hasInitializedRef.current) {
                    animateTo(offset, nextOffset);
                }
                else {
                    setOffset(nextOffset);
                }
            }
            else {
                // Offset has not changed - so the value must come down to where it was.
                updateValue(value, true);
            }
            hasInitializedRef.current = true;
        };
        const handleInternalChange = () => {
            let nextValue = (offset - minScaleOffset) * -1;
            nextValue = Math.floor(nextValue / ratio);
            prevOffsetRef.current = offset;
            updateValue(nextValue);
        };
        if (value !== prevValueRef.current && !movingLasts) {
            handleExternalChange();
        }
        else if (offset !== prevOffsetRef.current && !isAnimatingExternally) {
            // Suppress intermediate offsets emitted during programmatic `animateTo`.
            // Without this, every animation frame would call `onChange` with a
            // tween value and overwrite the caller's authoritative `value` mid-flight.
            handleInternalChange();
        }
    }, [
        value,
        offset,
        minScaleOffset,
        ratio,
        movingLasts,
        isAnimatingExternally,
        max,
        min,
        setOffset,
        animateTo,
        calcOffsetWithRatio,
        onChange,
    ]);
}
